import { ApiEndpoints } from "./apiEndpoints";
import { fetchData, fetchWithEnvelope, setHeaderOptions } from "./apiHelpers";
import { MessageFileStore } from "./fileStore.type";
import {
  AdminUserWithMessages,
  CreateMessageBody,
  MessageRow,
  MessagesWithPagination,
} from "./message.type";

type MessagesResponseEnvelope = {
  data: MessageRow[];
  meta?: {
    totalCount?: number;
    nextPage?: number | null;
  };
};

const normalizeMessageFiles = (message: MessageRow): MessageRow => {
  const incomingFiles =
    (message as unknown as { message_file_store?: MessageFileStore[] })
      .message_file_store ??
    (message as unknown as { files?: MessageFileStore[] }).files ??
    [];

  const normalizedFiles = Array.isArray(incomingFiles)
    ? incomingFiles
        .map((entry) => {
          if (!entry) return null;
          const fileStore =
            (entry as any).file_store ?? (entry as any).file ?? null;
          if (!fileStore) return null;
          return {
            ...entry,
            file_store: fileStore,
          } as MessageFileStore;
        })
        .filter(Boolean)
    : [];

  return {
    ...message,
    message_file_store: normalizedFiles as MessageFileStore[],
  };
};

const parseMessagesResponse = (
  response: MessagesResponseEnvelope
): MessagesWithPagination => {
  const messages = Array.isArray(response?.data)
    ? response.data.map(normalizeMessageFiles)
    : [];
  const totalCount =
    typeof response?.meta?.totalCount === "number"
      ? response.meta.totalCount
      : messages.length;
  const nextPage =
    typeof response?.meta?.nextPage === "number"
      ? response.meta.nextPage
      : null;

  return {
    messages,
    totalCount,
    nextPage,
  } satisfies MessagesWithPagination;
};

export const getChat = async (
  page: number = 1,
  count: number = 10
): Promise<MessagesWithPagination> => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      count: count.toString(),
    });

    const slug = `${ApiEndpoints.message}/?${queryParams.toString()}`;

    const options = await setHeaderOptions({
      method: "GET",
    });
    const chat = await fetchWithEnvelope<MessagesResponseEnvelope>(
      slug,
      options
    );

    return parseMessagesResponse(chat);
  } catch (err) {
    console.error("Error getting messages:", err);
    throw err;
  }
};

export const createMessage = async ({
  content,
  fileStoreIds,
}: {
  content: string;
  userId: string;
  fileStoreIds?: string[];
}): Promise<MessageRow> => {
  const slug = ApiEndpoints.message;

  const body: CreateMessageBody = {
    content,
    ...(fileStoreIds && { fileStoreIds }),
  };

  const options = await setHeaderOptions({
    method: "POST",
    body,
  });

  const res = await fetchData<MessageRow>(slug, options);
  return normalizeMessageFiles(res);
};

export const getGroupChatMessages = async (
  groupChatId: string,
  page: number = 1,
  count: number = 10
): Promise<MessagesWithPagination> => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    count: count.toString(),
  });

  const slug = `${
    ApiEndpoints.message
  }/${groupChatId}/group/?${queryParams.toString()}`;

  const options = await setHeaderOptions({ method: "GET" });
  const messages = await fetchWithEnvelope<MessagesResponseEnvelope>(
    slug,
    options
  );
  return parseMessagesResponse(messages);
};

export const createGroupChatMessage = async ({
  groupChatId,
  content,
  fileStoreIds,
}: {
  groupChatId: string;
  content: string;
  fileStoreIds?: string[];
}): Promise<MessageRow> => {
  const slug = `${ApiEndpoints.message}/${groupChatId}/group`;

  const body: CreateMessageBody = {
    content,
    ...(fileStoreIds && { fileStoreIds }),
  };
  const options = await setHeaderOptions({
    method: "POST",
    body,
  });
  const res = await fetchData<MessageRow>(slug, options);
  return normalizeMessageFiles(res);
};

export const getAdminUsersWithMessages = async (): Promise<
  AdminUserWithMessages[]
> => {
  const slug = `${ApiEndpoints.message}/admin/users-with-messages`;
  const options = await setHeaderOptions({ method: "GET" });
  return fetchData<AdminUserWithMessages[]>(slug, options);
};

export const getAdminUserMessages = async (
  userId: string,
  page: number = 1,
  count: number = 25
): Promise<MessagesWithPagination> => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    count: count.toString(),
  });

  const slug = `${ApiEndpoints.message}/admin/users/${userId}?${queryParams.toString()}`;
  const options = await setHeaderOptions({ method: "GET" });
  const response = await fetchData<MessagesWithPagination>(slug, options);
  const normalizedMessages = Array.isArray(response?.messages)
    ? response.messages.map(normalizeMessageFiles)
    : [];

  return {
    messages: normalizedMessages,
    totalCount:
      typeof response?.totalCount === "number"
        ? response.totalCount
        : normalizedMessages.length,
    nextPage:
      typeof response?.nextPage === "number" ? response.nextPage : null,
  } satisfies MessagesWithPagination;
};
